import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { API_BASE, parseJsonResponse, getUserErrorMessage } from '../../utils/apiBase';
import { authFetch } from '../../utils/apiAuth';
import SuperPanelShell from '../../components/super/SuperPanelShell';
import './superPanel.css';

const emptyForm = {
  name: '',
  email: '',
  phone: '',
  password: '',
  role: 'admin',
};

export default function AdminUserForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!isEdit) return undefined;

    let cancelled = false;

    async function loadUser() {
      setFetching(true);
      try {
        const response = await authFetch(`${API_BASE}/v1/user/panel`);
        const result = await parseJsonResponse(response);
        const user = (result.data || []).find((item) => item.id === id);
        if (!user) throw new Error('Admin user not found');
        if (!cancelled) {
          setForm({
            name: user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            password: '',
            role: user.role || 'admin',
          });
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(getUserErrorMessage(error, 'Unable to load admin user.'));
          navigate('/super/admins');
        }
      } finally {
        if (!cancelled) setFetching(false);
      }
    }

    loadUser();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, navigate]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        role: form.role,
      };
      if (form.password) payload.password = form.password;

      const url = isEdit
        ? `${API_BASE}/v1/user/panel/${id}`
        : `${API_BASE}/v1/user/panel`;

      if (!isEdit && !form.password) {
        toast.error('Password is required for new admin accounts');
        setLoading(false);
        return;
      }

      const response = await authFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isEdit ? payload : { ...payload, password: form.password }),
      });

      await parseJsonResponse(response);
      toast.success(isEdit ? 'Admin user updated' : 'Admin user created');
      navigate('/super/admins');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Unable to save admin user.'));
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <SuperPanelShell title="Loading..." breadcrumb="Super Panel">
        <p className="sp-muted">Loading admin user...</p>
      </SuperPanelShell>
    );
  }

  return (
    <SuperPanelShell
      title={isEdit ? 'Edit Admin User' : 'Create Admin User'}
      breadcrumb={`Super Panel • ${isEdit ? 'Edit' : 'Create'} Admin`}
      showBack
    >
      <Toaster
        position="top-center"
        toastOptions={{
          style: { background: '#12121f', color: '#f5d76e', border: '1px solid rgba(212,175,55,0.3)' },
        }}
      />

      <form className="sp-form-card" onSubmit={handleSubmit} autoComplete="off">
        {[
          ['name', 'Full Name', 'text'],
          ['email', 'Email Address', 'email'],
          ['phone', 'Phone Number', 'tel'],
        ].map(([field, label, type]) => (
          <div key={field} className="sp-form-group">
            <label className="sp-form-label" htmlFor={`sp-${field}`}>{label}</label>
            <input
              id={`sp-${field}`}
              type={type}
              className="sp-form-input"
              value={form[field]}
              onChange={(e) => handleChange(field, e.target.value)}
              required
              autoComplete="off"
            />
          </div>
        ))}

        <div className="sp-form-group">
          <label className="sp-form-label" htmlFor="sp-password">
            Password {isEdit && '(leave blank to keep current)'}
          </label>
          <input
            id="sp-password"
            type="password"
            className="sp-form-input"
            value={form.password}
            onChange={(e) => handleChange('password', e.target.value)}
            required={!isEdit}
            minLength={6}
            autoComplete="new-password"
          />
        </div>

        <div className="sp-form-group">
          <label className="sp-form-label" htmlFor="sp-role">Access Role</label>
          <select
            id="sp-role"
            className="sp-form-input"
            value={form.role}
            onChange={(e) => handleChange('role', e.target.value)}
          >
            <option value="admin">Admin — Standard panel access</option>
            <option value="superadmin">Super Admin — Full root access</option>
          </select>
        </div>

        <div className="sp-form-actions">
          <button type="button" className="sp-btn sp-btn--ghost" onClick={() => navigate('/super/admins')}>
            Cancel
          </button>
          <button type="submit" className="sp-btn sp-btn--gold" disabled={loading}>
            {loading ? 'Saving...' : isEdit ? 'Update Admin' : 'Create Admin'}
          </button>
        </div>
      </form>
    </SuperPanelShell>
  );
}
